import {XMarkIcon} from '@heroicons/react/24/outline'
import React, {FormEvent, useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Sponsor} from '../../hooks/useSponsors'
import Dialog from '../Dialog'
import FlatButton from '../FlatButton'
import TextInput from '../TextInput/AdminTextInput'
import Toast from '../Toast'
import InlineImageUpload from './components/InlineImageUpload'

type Props = Readonly<{
  sponsor: Sponsor
  open: boolean
  error?: Error | null
  onClose: () => void
  onConfirm: (
    sponsor: Sponsor,
    name: string,
    newImage?: File,
    imageRemoved?: boolean
  ) => void
  isBusy?: boolean
}>

function EditSponsorDialog({
  sponsor,
  open,
  error,
  onClose,
  onConfirm,
  isBusy = false
}: Props) {
  const intl = useIntl()
  const formId = 'edit-sponsor'
  const [state, setState] = useState<{
    name: string
    nameError: string | null
    selectedImage: File | null
    imageRemoved: boolean
  }>({
    name: sponsor.name,
    nameError: null,
    selectedImage: null,
    imageRemoved: false
  })

  // Reset state when sponsor changes or dialog opens
  useEffect(() => {
    if (open) {
      setState({
        name: sponsor.name,
        nameError: null,
        selectedImage: null,
        imageRemoved: false
      })
    }
  }, [open, sponsor.name])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    // Validate name
    if (!state.name.trim()) {
      setState(prev => ({
        ...prev,
        nameError: intl.formatMessage({id: 'admin.manageSponsors.required'})
      }))
    } else {
      setState(prev => ({...prev, nameError: null}))

      // Only pass selectedImage if user actually selected a new one
      // If they removed it, pass undefined (no image update)
      const imageToUpload = state.imageRemoved
        ? undefined
        : state.selectedImage || undefined
      onConfirm(sponsor, state.name.trim(), imageToUpload, state.imageRemoved)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="flex flex-col items-center">
        <div className="-mt-6 mb-6 flex w-full items-center justify-between">
          <Dialog.Title className="typography-headline-4">
            <FormattedMessage
              id="admin.manageSponsors.edit_sponsor_with_type"
              values={{
                type: intl.formatMessage({
                  id:
                    sponsor.type === 'NATIONAL'
                      ? 'admin.manageSponsors.national'
                      : 'admin.manageSponsors.community'
                })
              }}
            />
          </Dialog.Title>
          <button className="-mr-2 -mt-2" type="button" onClick={onClose}>
            <span className="sr-only">
              <FormattedMessage id="general.Close" />
            </span>
            <XMarkIcon aria-hidden className="size-7" />
          </button>
        </div>

        {error && (
          <div className="mb-4 w-full">
            <Toast variant="error">
              <FormattedMessage id="general.GenericError" />
            </Toast>
          </div>
        )}

        <form id={formId} onSubmit={handleSubmit} className="w-full space-y-8">
          <div className="flex items-start justify-between p-2 text-navy">
            <div className="typography-headline-5 after:content-[':']">
              <FormattedMessage id="admin.manageSponsors.sponsor_image" />
            </div>
            <InlineImageUpload
              onImageSelect={image => {
                setState(prev => ({
                  ...prev,
                  selectedImage: image,
                  imageRemoved: false
                }))
              }}
              onImageRemove={() => {
                setState(prev => ({
                  ...prev,
                  selectedImage: null,
                  imageRemoved: true
                }))
              }}
              currentImageUrl={
                state.selectedImage
                  ? URL.createObjectURL(state.selectedImage)
                  : state.imageRemoved
                    ? null
                    : sponsor.imageUrl || null
              }
            />
          </div>

          <TextInput
            id="sponsor-name"
            label={intl.formatMessage({id: 'admin.manageSponsors.name'})}
            value={state.name}
            onChange={e => setState(prev => ({...prev, name: e.target.value}))}
            errorMessage={state.nameError}
          />
        </form>

        <div className="mt-8 flex justify-center gap-4">
          <FlatButton variant="success" onClick={onClose} width="auto">
            <FormattedMessage id="admin.manageSponsors.cancel" />
          </FlatButton>
          <FlatButton
            variant="success"
            type="submit"
            form={formId}
            width="auto"
            disabled={isBusy}
          >
            <FormattedMessage id="general.save" />
          </FlatButton>
        </div>
      </div>
    </Dialog>
  )
}

export default EditSponsorDialog
